import { ScenarioChatFormSchema_Message_SelectionsType_None } from '../types'
import type {
  ScenarioChatFormQueryFragment,
  ScenarioChatFragment,
} from '../ScenarioChatForm.generated'
import type {
  ScenarioChatForm,
  ScenarioChatForm_Message,
  ScenarioChatForm_Message_BasicSelectionItem,
} from '../types'
import type * as EventType from './event'
import { nanoid } from 'nanoid'
import { createEventHook, createInjectionState, useCounter } from '@vueuse/core'
import { useAlert } from '@/libs/alertPortal'
import { computed, ref } from 'vue'
import * as mutative from 'mutative'
import { validateByValibot } from './validation'

type Props = {
  initialScenarioChat: ScenarioChatFragment | undefined
  query: ScenarioChatFormQueryFragment
  onSubmit: (values: ScenarioChatForm) => Promise<void>
}

const [useScenarioChatForm, _useInternalScenarioChatFormStore] =
  createInjectionState((props: Props) => {
    const alert = useAlert()

    const currentForUserIndex = useCounter(0)

    function mapToScenarioChatForm(
      scenarioChat: ScenarioChatFragment | undefined
    ): ScenarioChatForm {
      if (scenarioChat == undefined) {
        const clientOnlyId = nanoid()

        const newMessage: ScenarioChatForm_Message = {
          guid: undefined,
          clientOnlyId,
          forUserIndex: currentForUserIndex.inc(),
          isEntry: true,
          body: '',
          selections: {
            type: ScenarioChatFormSchema_Message_SelectionsType_None.literal,
          },
        }

        return {
          title: '',
          messages: {
            [clientOnlyId]: newMessage,
          },
        }
      }

      const messageArray: ScenarioChatForm_Message[] =
        scenarioChat.messages.map((m, i) => {
          return {
            guid: m.guid,
            clientOnlyId: nanoid(),
            forUserIndex: currentForUserIndex.inc(),
            isEntry: i === 0,
            body: m.body,
            selections: {
              type: ScenarioChatFormSchema_Message_SelectionsType_None.literal,
            },
          }
        })

      scenarioChat.messages.forEach((m, i) => {
        if (m.selectionsV2 == null) {
          return
        }

        switch (m.selectionsV2.__typename) {
          case 'ScenarioChatMessageBasicSelections':
            const basicSelections: ScenarioChatForm_Message_BasicSelectionItem[] =
              m.selectionsV2.basicSelections.map(s => {
                const nextMessage = (() => {
                  if (s.nextMessageIndex != null) {
                    const m = messageArray[s.nextMessageIndex]
                    if (!m) {
                      throw new Error('nextMessage is undefined')
                    }
                    return m
                  }
                  return undefined
                })()

                return {
                  guid: s.guid,
                  clientOnlyId: nanoid(),
                  label: s.label,
                  actions: s.actions.map(a => {
                    return {
                      type: 'AssignStaff',
                      staffGUID: a.assignedStaffGUID,
                    }
                  }),
                  nextScenarioChatMessageClientOnlyId:
                    nextMessage?.clientOnlyId,
                }
              })

            messageArray[i].selections = {
              type: 'BasicSelections',
              basicSelections,
            }
            break
          case 'ScenarioChatMessageBookingRequestSelections':
            messageArray[i].selections = {
              type: 'BookingRequestSelections',
              fallbackUrl: m.selectionsV2.fallbackUrl ?? '',
            }
            break
        }
      })

      return {
        title: scenarioChat.title,
        messages: Object.fromEntries(
          messageArray.map(m => [m.clientOnlyId, m])
        ),
      }
    }

    const formValues = ref<ScenarioChatForm>(
      mapToScenarioChatForm(props.initialScenarioChat)
    )

    // なんらかフォームに変更が加えられたかどうか
    const isDirty = ref(false)

    const changeEventHook = createEventHook<EventType.FormEventGroup>()

    /**
     * フォームの内容に変更があったときに呼び出す関数
     * EventHook をトリガーするなど役割があるため、formValues の更新のあと必ず呼び出してください
     * @function
     */
    const postProcessChange = (args: {
      eventGroup: EventType.FormEventGroup
    }) => {
      isDirty.value = true
      changeEventHook.trigger(args.eventGroup)
    }

    const updateMessage = (
      clientOnlyId: string,
      updater: (draft: mutative.Draft<ScenarioChatForm_Message>) => void
    ) => {
      type UpdateMessageEventGroup = [EventType.UpdateMessageEvent]

      const state = mutative.create(
        formValues.value,
        draft => {
          updater(draft.messages[clientOnlyId])
        },
        { strict: true }
      )

      formValues.value = state

      postProcessChange({
        eventGroup: {
          action: 'updateMessage',
          events: [
            {
              type: 'updateMessage',
              clientOnlyId,
              message: state.messages[clientOnlyId],
            },
          ] as UpdateMessageEventGroup,
        },
      })
    }

    const addNextMessage = (args: {
      messageClientOnlyId: string
      selectionClientOnlyId: string
    }) => {
      type AddNextMessageEventGroup = [
        EventType.AddMessageEvent,
        EventType.UpdateMessageEvent,
        EventType.AddConnectionEvent,
      ]

      const nextMessageClientOnlyId = nanoid()
      const newMessage: ScenarioChatForm_Message = {
        guid: undefined,
        clientOnlyId: nextMessageClientOnlyId,
        forUserIndex: currentForUserIndex.inc(),
        isEntry: false, // 追加されるメッセージは entry ではない
        body: '',
        selections: {
          type: ScenarioChatFormSchema_Message_SelectionsType_None.literal,
        },
      }

      const state = mutative.create(
        formValues.value,
        draft => {
          const updatedMessage = draft.messages[args.messageClientOnlyId]
          if (!updatedMessage) {
            throw new Error('存在しない')
          }

          if (updatedMessage.selections.type !== 'BasicSelections') {
            throw new Error('selections の type が BasicSelections ではない')
          }

          updatedMessage.selections.basicSelections =
            updatedMessage.selections.basicSelections.map<ScenarioChatForm_Message_BasicSelectionItem>(
              selection => {
                if (selection.clientOnlyId === args.selectionClientOnlyId) {
                  return {
                    ...selection,
                    nextScenarioChatMessageClientOnlyId:
                      nextMessageClientOnlyId,
                  }
                }
                return selection
              }
            )

          draft.messages[nextMessageClientOnlyId] = newMessage
        },
        { strict: true }
      )

      formValues.value = state

      postProcessChange({
        eventGroup: {
          action: 'addNextMessage',
          events: [
            {
              type: 'addMessage',
              sourceMessageClientOnlyId: args.messageClientOnlyId,
              sourceSelectionClientOnlyId: args.selectionClientOnlyId,
              newMessage,
            },
            {
              type: 'updateMessage',
              clientOnlyId: args.messageClientOnlyId,
              message: state.messages[args.messageClientOnlyId],
            },
            {
              type: 'addConnection',
              sourceMessageClientOnlyId: args.messageClientOnlyId,
              sourceSelectionClientOnlyId: args.selectionClientOnlyId,
              targetMessageClientOnlyId: nextMessageClientOnlyId,
            },
          ] as AddNextMessageEventGroup,
        },
      })
    }

    const deleteMessage = (clientOnlyId: string) => {
      type DeleteMessageEventGroup = [
        EventType.RemoveConnectionsEvent,
        EventType.RemoveMessageEvent,
        EventType.UpdateMessagesEvent,
      ]

      const removeConnectionsEvent: EventType.RemoveConnectionsEvent = {
        type: 'removeConnections',
        removes: [],
      }

      const updatedMessages: Record<string, ScenarioChatForm_Message> = {}

      formValues.value = mutative.create(
        formValues.value,
        draft => {
          // メッセージそのものを削除する
          delete draft.messages[clientOnlyId]

          // 削除されるメッセージを遷移先にしている設定を消去する
          Object.values(draft.messages).forEach(message => {
            if (message.selections.type !== 'BasicSelections') {
              return
            }

            let updated = false

            message.selections.basicSelections =
              message.selections.basicSelections.map(selection => {
                if (
                  selection.nextScenarioChatMessageClientOnlyId === clientOnlyId
                ) {
                  removeConnectionsEvent.removes.push({
                    sourceMessageClientOnlyId: message.clientOnlyId,
                    sourceSelectionClientOnlyId: selection.clientOnlyId,
                    targetMessageClientOnlyId: clientOnlyId,
                  })

                  updated = true

                  return {
                    ...selection,
                    nextScenarioChatMessageClientOnlyId: undefined,
                  }
                }

                return selection
              })

            if (updated) {
              updatedMessages[message.clientOnlyId] = mutative.current(message)
            }
          })
        },
        { strict: true }
      )

      postProcessChange({
        eventGroup: {
          action: 'deleteMessage',
          events: [
            removeConnectionsEvent,
            {
              type: 'removeMessage',
              clientOnlyId,
            },
            {
              type: 'updateMessages',
              updates: updatedMessages,
            },
          ] as DeleteMessageEventGroup,
        },
      })
    }

    const connect = (args: {
      sourceMessageClientOnlyId: string
      sourceSelectionClientOnlyId: string
      targetMessageClientOnlyId: string
    }) => {
      // パターンが2つある
      // 既存の接続があったかどうかで RemoveConnectionEvent の有無が変わる
      type ConnectEventGroup =
        | [
            EventType.RemoveConnectionEvent,
            EventType.UpdateMessageEvent,
            EventType.AddConnectionEvent,
          ]
        | [EventType.UpdateMessageEvent, EventType.AddConnectionEvent]

      let previousTargetMessageClientOnlyId: string | undefined = undefined

      const state = mutative.create(
        formValues.value,
        draft => {
          const sourceMessage = draft.messages[args.sourceMessageClientOnlyId]
          if (!sourceMessage) {
            throw new Error('存在しない')
          }

          if (sourceMessage.selections.type !== 'BasicSelections') {
            throw new Error('selections の type が BasicSelections ではない')
          }

          const sourceSelection = sourceMessage.selections.basicSelections.find(
            s => s.clientOnlyId === args.sourceSelectionClientOnlyId
          )
          if (!sourceSelection) {
            throw new Error('存在しない')
          }

          previousTargetMessageClientOnlyId =
            sourceSelection.nextScenarioChatMessageClientOnlyId

          sourceSelection.nextScenarioChatMessageClientOnlyId =
            args.targetMessageClientOnlyId
        },
        { strict: true }
      )

      formValues.value = state

      const events: ConnectEventGroup = (() => {
        if (previousTargetMessageClientOnlyId) {
          return [
            {
              type: 'removeConnection',
              sourceMessageClientOnlyId: args.sourceMessageClientOnlyId,
              sourceSelectionClientOnlyId: args.sourceSelectionClientOnlyId,
              targetMessageClientOnlyId: previousTargetMessageClientOnlyId,
            },
            {
              type: 'updateMessage',
              clientOnlyId: args.sourceMessageClientOnlyId,
              message: state.messages[args.sourceMessageClientOnlyId],
            },
            {
              type: 'addConnection',
              sourceMessageClientOnlyId: args.sourceMessageClientOnlyId,
              sourceSelectionClientOnlyId: args.sourceSelectionClientOnlyId,
              targetMessageClientOnlyId: args.targetMessageClientOnlyId,
            },
          ]
        }

        return [
          {
            type: 'updateMessage',
            clientOnlyId: args.sourceMessageClientOnlyId,
            message: state.messages[args.sourceMessageClientOnlyId],
          },
          {
            type: 'addConnection',
            sourceMessageClientOnlyId: args.sourceMessageClientOnlyId,
            sourceSelectionClientOnlyId: args.sourceSelectionClientOnlyId,
            targetMessageClientOnlyId: args.targetMessageClientOnlyId,
          },
        ]
      })()

      postProcessChange({
        eventGroup: {
          action: 'connect',
          events,
        },
      })
    }

    const disconnect = (args: {
      sourceMessageClientOnlyId: string
      sourceSelectionClientOnlyId: string
    }) => {
      type DisconnectEventGroup = [
        EventType.RemoveConnectionEvent,
        EventType.UpdateMessagesEvent,
      ]

      let removeConnectionEvent: EventType.RemoveConnectionEvent

      const updatedMessagesEvent: EventType.UpdateMessagesEvent = {
        type: 'updateMessages',
        updates: {}, // この値はあとでセットされる
      }

      formValues.value = mutative.create(
        formValues.value,
        draft => {
          const sourceMessage = draft.messages[args.sourceMessageClientOnlyId]
          if (!sourceMessage) {
            throw new Error('存在しない')
          }

          if (sourceMessage.selections.type !== 'BasicSelections') {
            throw new Error('selections の type が BasicSelections ではない')
          }

          const selection = sourceMessage.selections.basicSelections.find(
            s => s.clientOnlyId === args.sourceSelectionClientOnlyId
          )
          if (!selection) {
            throw new Error('存在しない')
          }
          if (!selection.nextScenarioChatMessageClientOnlyId) {
            throw new Error('nextScenarioChatMessageClientOnlyId が undefined')
          }

          removeConnectionEvent = {
            type: 'removeConnection',
            sourceMessageClientOnlyId: args.sourceMessageClientOnlyId,
            sourceSelectionClientOnlyId: args.sourceSelectionClientOnlyId,
            targetMessageClientOnlyId:
              selection.nextScenarioChatMessageClientOnlyId,
          }

          selection.nextScenarioChatMessageClientOnlyId = undefined

          updatedMessagesEvent.updates[args.sourceMessageClientOnlyId] =
            mutative.current(sourceMessage)
        },
        { strict: true }
      )

      postProcessChange({
        eventGroup: {
          action: 'disconnect',
          events: [
            removeConnectionEvent!,
            updatedMessagesEvent,
          ] as DisconnectEventGroup,
        },
      })
    }

    const isSubmitting = ref(false)

    const submit = async () => {
      if (isSubmitting.value) {
        return
      }

      isSubmitting.value = true

      const schemaValidationResult = validateByValibot(formValues.value)
      if (schemaValidationResult.type === 'error') {
        alert.error(schemaValidationResult.errorMessages.join('\n'))
        isSubmitting.value = false
        return
      }

      // 接続されていないメッセージのバリデーション
      const invalidMessageClientOnlyIds = Array.from(
        messageHasConnectionAsTargetMap.value.entries()
      )
        .filter(([, hasConnection]) => !hasConnection)
        .map(([clientOnlyId]) => clientOnlyId)

      if (invalidMessageClientOnlyIds.length > 0) {
        return {
          type: 'invalid',
          invalidMessageClientOnlyIds,
        }
      }

      try {
        await props.onSubmit(formValues.value)
        isDirty.value = false
      } finally {
        isSubmitting.value = false
      }
    }

    const messageHasConnectionAsTargetMap = computed<Map<string, boolean>>(
      () => {
        const messageAsTargetConnectionMap = new Map(
          Object.entries(formValues.value.messages).map(
            ([clientOnlyId, message]) => {
              // 最初のメッセージは invalid でないように、hasConnection: true とする
              const hasConnection = message.isEntry
              return [clientOnlyId, hasConnection]
            }
          )
        )

        Object.values(formValues.value.messages).forEach(message => {
          if (message.selections.type === 'BasicSelections') {
            message.selections.basicSelections.forEach(selection => {
              if (selection.nextScenarioChatMessageClientOnlyId) {
                messageAsTargetConnectionMap.set(
                  selection.nextScenarioChatMessageClientOnlyId,
                  true
                )
              }
            })
          }
        })

        return messageAsTargetConnectionMap
      }
    )

    return {
      formValues,
      isDirty,
      isSubmitting,
      updateMessage,
      addNextMessage,
      deleteMessage,
      connect,
      disconnect,
      onChange: changeEventHook.on,
      submit,
      messageHasConnectionAsTargetMap,
      staffs: props.query.staffsByPlanner,
    }
  })

function useInternalScenarioChatFormStore() {
  // non-nullable にするための composable
  // 参考
  // https://vueuse.org/shared/createInjectionState/#usage

  const store = _useInternalScenarioChatFormStore()
  if (store == undefined) {
    throw new Error('ScenarioChatFormStore is not provided')
  }

  return store
}

export { useScenarioChatForm, useInternalScenarioChatFormStore }
